import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  FoodItem,
  FoodItemInsert,
  FoodItemUpdate,
  FoodItemWithRelations,
} from './types';

const QUERY_KEY = ['food-items'] as const;

const SELECT_WITH_RELATIONS = `
  *,
  category:food_categories(id, label, icon, color)
` as const;

/**
 * Liste tous les items du menu bouffe avec leur catégorie. Triés par
 * favori d'abord, puis par sort_order de la catégorie, puis par nom.
 */
export function useFoodItems() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<FoodItemWithRelations[]> => {
      const { data, error } = await supabase
        .from('food_items')
        .select(SELECT_WITH_RELATIONS)
        .order('is_favorite', { ascending: false })
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as FoodItemWithRelations[];
    },
  });
}

/**
 * Hook public : retourne uniquement les food_items marqués is_public=true,
 * triés par catégorie puis par nom. Accessible aux visiteurs anonymes.
 */
export function usePublicFoodItems() {
  return useQuery({
    queryKey: ['food-items', 'public'],
    queryFn: async (): Promise<FoodItemWithRelations[]> => {
      const { data, error } = await supabase
        .from('food_items')
        .select(SELECT_WITH_RELATIONS)
        .eq('is_public', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as FoodItemWithRelations[];
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateFoodItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FoodItemInsert): Promise<FoodItem> => {
      const { data, error } = await supabase
        .from('food_items')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as FoodItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useUpdateFoodItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: FoodItemUpdate;
    }): Promise<FoodItem> => {
      const { data, error } = await supabase
        .from('food_items')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as FoodItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteFoodItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('food_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useToggleFoodPublic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      next,
    }: {
      id: string;
      next: boolean;
    }): Promise<void> => {
      const { error } = await supabase
        .from('food_items')
        .update({ is_public: next })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useToggleFoodFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      next,
    }: {
      id: string;
      next: boolean;
    }): Promise<void> => {
      const { error } = await supabase
        .from('food_items')
        .update({ is_favorite: next })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
